import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialog,
} from '@angular/material/dialog';
import {
  Firestore,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  collection,
  query,
  getDocs,
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
} from '@angular/fire/storage';
import { UploadLpoDialogComponent } from '../../views/auth/booking-orders/upload-lpo-dialog/upload-lpo-dialog.component';
import { RejectionReasonDialogComponent } from '../rejection-reason-dialog/rejection-reason-dialog.component';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';
import { BookingLockService } from 'src/app/shared/services/booking-lock.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { RealtimeBookingService } from 'src/app/shared/services/realtime-booking.service';
import { DatePipe, DecimalPipe } from '@angular/common';
import { DocumentEditorDialogBaseComponent } from 'src/app/shared/base/document-editor-dialog-base';

// PDFMake Imports
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { TDocumentDefinitions, Content } from 'pdfmake/interfaces';

// html2canvas and jsPDF for PDF generation
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const fonts = pdfFonts as any;
(pdfMake as any).vfs =
  fonts.pdfMake?.vfs ||
  fonts.vfs ||
  fonts.default?.pdfMake?.vfs ||
  fonts.default?.vfs;

@Component({
  selector: 'app-lpo-viewer-dialog',
  templateUrl: './lpo-viewer-dialog.component.html',
  styleUrls: ['./lpo-viewer-dialog.component.scss'],
  providers: [DatePipe, DecimalPipe],
})
export class LpoViewerDialogComponent extends DocumentEditorDialogBaseComponent implements OnInit, OnDestroy {
  vendorDetails: any = null;
  customerDetails: any = null;
  duration: string = '';
  lpoDate: Date = new Date();
  isVendor = false;
  isSample = false;
  sampleData: any = null;
  customerLogoBase64: string | null = null;
  editingBase = false;
  editingDelivery = false;

  @ViewChild('sigCanvas') sigCanvas!: ElementRef<HTMLCanvasElement>;
  private ctx!: CanvasRenderingContext2D;
  private isDrawing = false;
  selectedTabIndex = 0;
  deliveryCharge: number = 0;
  discount: number = 0;

  baseAmount: number = 0;
  isLoading: boolean = true;
  showInitialChoice: boolean = false;
  showUploadSection: boolean = false;

  // Upload related
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  selectedFile: File | null = null;
  uploadPreviewUrl: string | null = null;
  uploadProgress = 0;

  /** Populated when an LPO is generated or loaded from Firestore. */
  digitalSignature: { signedBy: string; signedAt: string; hash: string; bookingId: string; refNo: string } | null = null;

  /** True once the customer ticks the declaration checkbox. */
  consentGiven = false;

  /** Saved record of the consent — persisted to Firestore and shown on print. */
  consentRecord: { customerName: string; agreedAt: string } | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { booking: any; isVendor?: boolean; mode?: string; showUploadDirectly?: boolean },
    private dialog: MatDialog,
    protected override dialogRef: MatDialogRef<LpoViewerDialogComponent>,
    private afs: Firestore,
    private storage: Storage,
    protected override ux: UxserviceService,
    private bookingLock: BookingLockService,
    private rtBooking: RealtimeBookingService,
    private notificationService: NotificationService,
    private auth: Auth,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
    private cdr: ChangeDetectorRef,
  ) {
    super(dialogRef, ux);
  }

  private rtSub?: any;

  async ngOnInit() {
    this.isVendor = (this.data as any).isVendor || false;
    this.isSample = (this.data as any).isSample || false;

    console.log('🚀 LPO Viewer Dialog initialized with booking:', {
      bookingId: this.data.booking.id,
      hasLpo: !!this.data.booking.lpo,
      lpoData: this.data.booking.lpo,
      isVendor: this.isVendor,
      isSample: this.isSample,
    });

    if (this.isSample) {
      this.sampleData = (this.data as any).sampleData;
      this.customerDetails = this.sampleData.customerDetails;
      this.vendorDetails = this.sampleData.vendorDetails;

      if (this.customerDetails?.logoUrl) {
        this.makeImageCircular(this.customerDetails.logoUrl)
          .then((b64) => (this.customerLogoBase64 = b64))
          .catch((e) => console.warn('Failed to load logo for PDF', e));
      }

      // Ensure telephoneNumber is available in sample customer details
      if (
        this.sampleData.customerDetails &&
        !this.sampleData.customerDetails.telephoneNumber
      ) {
        this.sampleData.customerDetails.telephoneNumber = '';
      }

      this.calculateDuration();
      this.isLoading = false;
      return;
    }

    if (this.data.booking.lpoDate) {
      const d = this.data.booking.lpoDate;
      this.lpoDate = d.toDate ? d.toDate() : new Date(d);
    } else {
      this.lpoDate = new Date();
    }

    if (!this.data.booking.customer) {
      this.data.booking.customer = {};
    }

    if (!this.data.booking.customer.location && this.data.booking.location) {
      this.data.booking.customer.location = this.data.booking.location;
    }

    const pType =
      this.data.booking.priceType ||
      this.data.booking.commissionType ||
      this.data.booking.rateType;
    if (!this.data.booking.customer.priceType && pType) {
      this.data.booking.customer.priceType = pType;
    }

    // Common LPO init parameters
    this.calculateDuration();
    if (this.data && (this.data as any).isVendor) {
      this.isVendor = true;
    }
    this.initRealtimeSync();
    this.markBaseline();

    this.deliveryCharge =
      Number(this.data.booking.deliveryCharge) ||
      Number(this.data.booking.quotation?.deliveryCharge) ||
      0;
    this.baseAmount = Number(this.data.booking.totalAmount) || 0;

    if (this.data.booking.lpo?.digitalSignature) {
      this.digitalSignature = this.data.booking.lpo.digitalSignature;
    }

    if (this.data.booking.lpo?.consentRecord) {
      this.consentRecord = this.data.booking.lpo.consentRecord;
      this.consentGiven = true;
    }

    const isUploadView = !this.data.booking.lpoGenerated && !this.data.booking.LPO;

    if (isUploadView) {
      this.isLoading = false;
      this.showUploadSection = true;
      this.showInitialChoice = false;
      // Load details asynchronously in the background
      Promise.all([this.loadVendorDetails(), this.loadCustomerDetails()]).catch((e) =>
        console.error('Error loading LPO details in background:', e)
      );
    } else {
      try {
        this.isLoading = true;
        await Promise.all([this.loadVendorDetails(), this.loadCustomerDetails()]);
      } catch (error) {
        console.error('Error loading LPO details:', error);
      } finally {
        this.isLoading = false;
      }
      this.showUploadSection = false;
      this.showInitialChoice = false;
    }
  }

  // ─────────────────────────────────────────────
  //  CONSENT TOGGLE
  // ─────────────────────────────────────────────

  toggleConsent() {
    if (this.data.booking.lpoGenerated) return; // lock after sent
    this.consentGiven = !this.consentGiven;
  }

  // ─────────────────────────────────────────────
  //  DIGITAL SIGNATURE HELPERS
  // ─────────────────────────────────────────────

  private async generateIntegrityHash(payload: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(payload);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const fullHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return `${fullHex.slice(0, 4)}-${fullHex.slice(4, 8)}-${fullHex.slice(8, 12)}-${fullHex.slice(12, 16)}`.toUpperCase();
  }

  async buildDigitalSignature() {
    const now = new Date();
    const customerId = this.data.booking.bywhom || this.data.booking.customerId || 'unknown';

    const payload = [
      this.data.booking.id,
      customerId,
      this.baseAmount,
      this.deliveryCharge,
      this.getFinalTotal(),
      now.toISOString(),
    ].join('|');

    const hash = await this.generateIntegrityHash(payload);

    return {
      signedBy: this.customerDetails?.companyName || 'Customer',
      signedAt: now.toISOString(),
      hash,
      bookingId: this.data.booking.id,
      refNo: 'PO-' + this.data.booking.id.slice(-6).toUpperCase(),
    };
  }

  onGenerateClick() {
    this.showInitialChoice = false;
  }

  initRealtimeSync() {
    this.rtSub = this.rtBooking
      .watchBooking(this.data.booking.id)
      .subscribe((rtData: any) => {
        if (rtData) {
          if (rtData.status) {
            this.data.booking.status = rtData.status;
          }
          if (rtData.lpo) {
            const lpo = rtData.lpo;
            if (lpo.generated !== undefined)
              this.data.booking.lpoGenerated = lpo.generated;
            if (lpo.rejected !== undefined)
              this.data.booking.lpoRejected = lpo.rejected;
            if (lpo.approved !== undefined)
              this.data.booking.lpoApproved = lpo.approved;
            if (lpo.url) this.data.booking.LPO = lpo.url;

            if (lpo.customerSignature) {
              if (!this.data.booking.lpo) this.data.booking.lpo = {};
              this.data.booking.lpo.customerSignature = lpo.customerSignature;
              this.data.booking.customerSignatureUrl = lpo.customerSignature;
              if (this.customerDetails)
                this.customerDetails.signatureUrl = lpo.customerSignature;
            }
            if (lpo.customerSignatureBase64) {
              if (!this.data.booking.lpo) this.data.booking.lpo = {};
              this.data.booking.lpo.customerSignatureBase64 =
                lpo.customerSignatureBase64;
              this.data.booking.customerSignatureBase64 =
                lpo.customerSignatureBase64;
              if (this.customerDetails)
                this.customerDetails.signatureBase64 =
                  lpo.customerSignatureBase64;
            }
            if (lpo.deliveryCharge !== undefined)
              this.deliveryCharge = lpo.deliveryCharge;
            if (lpo.baseAmount !== undefined) this.baseAmount = lpo.baseAmount;
            if (lpo.rejectionReason !== undefined)
              this.data.booking.lpoRejectionReason = lpo.rejectionReason;
          }
          this.cdr.detectChanges();
        }
      });
  }

  ngOnDestroy() {
    this.rtSub?.unsubscribe();
  }

  async loadVendorDetails() {
    const vendorId = this.data.booking.equipment?.bywhom;
    if (!vendorId) return;

    const vendorRef = doc(this.afs, 'users-list', vendorId);
    const snap = await getDoc(vendorRef);
    if (snap.exists()) {
      const userData = snap.data();
      const accountDetails = userData['accountdetails'] || {};

      this.vendorDetails = {
        displayName: userData['displayName'] || userData['companyName'] || 'Vendor',
        companyname:
          accountDetails.companyname ||
          userData['companyName'] ||
          userData['displayName'] ||
          'Vendor',
        address:
          accountDetails.companyAddress ||
          accountDetails.address ||
          userData['companyAddress'] ||
          userData['address'] ||
          '',
        phoneNumber:
          accountDetails.phoneNumber ||
          userData['phoneNumber'] ||
          userData['phone'] ||
          '',
        emailid:
          accountDetails.emailid ||
          userData['email'] ||
          userData['emailid'] ||
          '',
      };
    }
  }

  async loadCustomerDetails() {
    // If it's a vendor manual booking, customer details are entered directly on the form
    if (this.data.booking.isVendorOwnBooking) {
      this.customerDetails = {
        companyName: this.data.booking.customer?.companyName || 'Customer',
        companyDetails: this.data.booking.customer?.companyAddress || this.data.booking.customer?.location || '',
        phoneNumber: this.data.booking.customer?.phone || '',
        telephoneNumber: '',
        email: this.data.booking.customer?.email || '',
        logoUrl: null,
        companyLogoBase64: null,
        lpoHeader: '',
        lpoTerms: '',
        lpoFooter: '',
        signatureUrl: null,
        signatureBase64: null,
        trn: '',
      };
      return;
    }

    const customerId = this.data.booking.bywhom;
    if (!customerId) return;

    const custRef = doc(this.afs, 'users-list', customerId);
    const snap = await getDoc(custRef);
    if (snap.exists()) {
      const userData = snap.data();
      const lpoTemplate = userData['lpoTemplate'] || {};

      let signatureUrl =
        this.data.booking.customerSignatureUrl ||
        this.data.booking.lpo?.customerSignature ||
        lpoTemplate.signatureUrl ||
        null;

      let signatureBase64 =
        this.data.booking.customerSignatureBase64 ||
        this.data.booking.lpo?.customerSignatureBase64 ||
        null;

      console.log('📝 Loading customer details:', {
        bookingId: this.data.booking.id,
        hasLpoObject: !!this.data.booking.lpo,
        signatureUrl: signatureUrl,
        signatureBase64: signatureBase64
          ? signatureBase64.substring(0, 50) + '...'
          : null,
        signatureBase64Length: signatureBase64?.length || 0,
      });

      this.customerDetails = {
        companyName:
          userData['companyName'] || userData['displayName'] || 'Customer',
        companyDetails:
          userData['companyAddress'] || userData['companyDetails'] || '',
        phoneNumber: userData['phoneNumber'] || '',
        telephoneNumber:
          lpoTemplate.telephoneNumber || userData['telephone'] || '', // Added telephone
        email: userData['email'] || '',
        logoUrl: userData['companyLogoUrl'] || null,
        companyLogoBase64: userData['companyLogoBase64'] || null, // NEW: Added base64 field

        lpoHeader: lpoTemplate.header || '',
        lpoTerms: lpoTemplate.terms || '',
        lpoFooter: lpoTemplate.footer || '',
        signatureUrl: signatureUrl,
        signatureBase64: signatureBase64,
        trn: userData['businessLicenseNo'] || userData['trn'] || '',
      };

      if (this.customerDetails?.logoUrl) {
        this.makeImageCircular(this.customerDetails.logoUrl)
          .then((b64) => (this.customerLogoBase64 = b64))
          .catch((e) => console.warn('Failed to load logo for PDF', e));
      }
    }
  }

  calculateDuration() {
    const booking = this.data.booking;

    // Handle Per Trip logic
    if (booking.customer?.priceType === 'per_trip') {
      const rate = booking.rate || 0;
      const total = booking.totalAmount || 0;
      const trips = rate > 0 ? Math.round(total / rate) : 1;
      this.duration = `${trips} Trip${trips !== 1 ? 's' : ''}`;
      return;
    }

    const start = booking.customer?.startDate?.toDate() ?? null;
    const end = booking.customer?.endDate?.toDate() ?? null;
    if (!start || !end) return;

    const diff = Math.ceil(
      (end.getTime() - start.getTime()) / (1000 * 3600 * 24),
    );
    this.duration = diff > 0 ? `${diff} days` : '1 day';
  }

  getEffectiveRate(): number {
    const booking = this.data.booking;
    const base = Number(this.baseAmount) || 0;

    if (booking.customer?.priceType === 'per_trip') {
      const tripsStr = this.duration.split(' ')[0];
      const trips = parseInt(tripsStr) || 1;
      return base / trips;
    }

    const start = booking.customer?.startDate?.toDate() ?? null;
    const end = booking.customer?.endDate?.toDate() ?? null;
    if (!start || !end) return base;

    let units = Math.ceil(
      (end.getTime() - start.getTime()) / (1000 * 3600 * 24),
    );
    if (units <= 0) units = 1;

    if (booking.customer?.priceType === 'monthly') {
      units = Math.ceil(units / 30);
    }

    return base / units;
  }

  resetAdjustment() {
    this.deliveryCharge =
      Number(this.data.booking.deliveryCharge) ||
      Number(this.data.booking.quotation?.deliveryCharge) ||
      0;
    this.baseAmount = Number(this.data.booking.totalAmount) || 0;
  }

  getFinalTotal(): number {
    const subtotal = Number(this.baseAmount) || 0;
    const delivery = Number(this.deliveryCharge) || 0;
    return subtotal + delivery;
  }

  isSaving = false;

  openUploadLpoDialog() {
    this.showUploadSection = true;
    this.showInitialChoice = false;
  }

  onBackToChoice() {
    this.close();
  }

  openFilePicker() {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    if (!(file.type.includes('pdf') || file.type.includes('image'))) {
      this.ux.showError('Please select a PDF or image file.');
      return;
    }

    this.selectedFile = file;
    this.uploadPreviewUrl = URL.createObjectURL(file);
  }

  async uploadLPO() {
    if (!this.selectedFile) {
      this.ux.showError('Please select a file to upload.');
      return;
    }

    this.ux.showOverlay('Uploading LPO...');
    this.isSaving = true;
    try {
      const filePath = `LPOs/${this.data.booking.id}/${Date.now()}_${this.selectedFile.name}`;
      const fileRef = ref(this.storage, filePath);
      const uploadResult = await uploadBytes(fileRef, this.selectedFile);
      const downloadURL = await getDownloadURL(uploadResult.ref);

      this.data.booking.LPO = downloadURL;

      // Update Firestore booking document
      const bookingRef = doc(this.afs, 'booking-list', this.data.booking.id);
      await updateDoc(bookingRef, {
        LPO: downloadURL,
        lpoUrl: downloadURL
      });

      // Update Realtime DB
      await this.rtBooking.updateLpo(
        this.data.booking.id,
        { url: downloadURL },
        this.auth.currentUser?.uid,
      );

      // Handle status transitions
      const snapshot = await getDoc(bookingRef);
      if (snapshot.exists()) {
        const bookingData = snapshot.data();
        const currentStatus = bookingData['status'] || 'Pending';

        if (currentStatus.toLowerCase() === 'confirmed') {
          await updateDoc(bookingRef, { status: 'Started' });
          await this.rtBooking.updateStatus(this.data.booking.id, 'Started');
        }

        const vendorId = bookingData['equipment']?.bywhom || bookingData['vendorId'];
        if (vendorId && !this.data.booking.isVendorOwnBooking) {
          this.notificationService.sendNotification(vendorId, {
            title: 'LPO Uploaded',
            message: `Customer has uploaded an LPO for Booking #${this.data.booking.id}`,
            type: 'success',
            link: `/business/booking/booking-details/${this.data.booking.id}`,
            bookingId: this.data.booking.id,
          });
        }
      }

      // Register document version entry in decoupled lpos collection
      try {
        const lNum = this.data.booking.lpoNumber || 'LPO-GEN';
        const versionsCol = collection(this.afs, `booking-list/${this.data.booking.id}/lpos/${lNum}/versions`);
        const qSnap = await getDocs(query(versionsCol));
        const bookingDocs: any[] = [];
        qSnap.forEach(d => {
          const dData = d.data();
          if (!dData['isDeleted']) bookingDocs.push({ id: d.id, ...dData });
        });

        for (const oldDoc of bookingDocs) {
          if (oldDoc.isCurrent) {
            await updateDoc(doc(this.afs, `booking-list/${this.data.booking.id}/lpos/${lNum}/versions`, oldDoc.id), {
              isCurrent: false
            });
          }
        }

        const version = bookingDocs.length + 1;
        const now = new Date();
        const versionDoc = {
          id: String(version),
          type: 'LPO',
          category: 'Uploaded',
          name: this.selectedFile.name || `L_${lNum}_v${version}`,
          documentNumber: lNum,
          version: version,
          url: downloadURL,
          mimeType: this.selectedFile.type || 'application/pdf',
          extension: 'pdf',
          createdAt: now,
          createdBy: this.auth.currentUser?.uid || 'temp_vendor',
          status: 'Approved',
          isCurrent: true,
          displayOrder: 3,
          actionHistory: [{
            action: 'Uploaded',
            performedBy: this.auth.currentUser?.uid || 'temp_vendor',
            performedAt: now
          }]
        };

        await setDoc(doc(this.afs, `booking-list/${this.data.booking.id}/lpos/${lNum}/versions`, String(version)), versionDoc);
        await setDoc(doc(this.afs, `booking-list/${this.data.booking.id}/lpos`, lNum), {
          documentNumber: lNum,
          type: 'LPO',
          updatedAt: now,
          activeVersion: version
        }, { merge: true });
      } catch (vErr) {
        console.error('Error saving uploaded LPO version entry:', vErr);
      }

      this.ux.showSuccess('LPO uploaded successfully!');
      this.dialogRef.close({
        action: 'uploaded',
        pdfUrl: downloadURL
      });
    } catch (err) {
      console.error('❌ Upload error:', err);
      this.ux.showError('Failed to upload LPO.');
    } finally {
      this.isSaving = false;
      this.ux.instantCloseOverlay();
    }
  }

  async confirmSaveLpo() {
    if (!this.consentGiven) {
      this.ux.showError('Please tick the declaration checkbox before sending.');
      return;
    }

    this.isSaving = true;
    try {
      // Build digital signature before saving
      this.digitalSignature = await this.buildDigitalSignature();

      // Build consent record
      this.consentRecord = {
        customerName: this.customerDetails?.companyName || 'Customer',
        agreedAt: new Date().toISOString(),
      };

      await this.saveLpoStatus();

      const vendorId = this.data.booking.equipment?.bywhom || this.data.booking.vendorId;

      if (vendorId && !this.data.booking.isVendorOwnBooking) {
        this.notificationService.sendNotification(vendorId, {
          title: 'LPO Submitted',
          message: `Customer has submitted an LPO for Booking #${this.data.booking.id}`,
          type: 'success',
          link: `/business/booking/booking-details/${this.data.booking.id}`,
          bookingId: this.data.booking.id,
        });
      }

      this.dialogRef.close({
        action: 'generated',
        pdfUrl: this.data.booking.LPO || this.data.booking.lpoUrl || null,
        documentNumber: this.data.booking.lpoNumber || 'LPO-GEN'
      });
    } catch (error) {
      console.error('Error saving LPO:', error);
      this.isSaving = false;
    }
  }

  async saveLpoStatus() {
    const now = new Date();
    const bookingRef = doc(this.afs, 'booking-list', this.data.booking.id);

    const updateData: any = {
      lpoGenerated: true,
      lpoDate: now,
      lpoRejected: false,
      lpoRejectionReason: null,
      deliveryCharge: Number(this.deliveryCharge) || 0,
      totalAmount: Number(this.baseAmount) || 0,
    };

    // Customer signature URL
    if (this.customerDetails?.signatureUrl) {
      updateData['lpo.customerSignature'] = this.customerDetails.signatureUrl;
      if (!this.data.booking.lpo) this.data.booking.lpo = {};
      this.data.booking.lpo.customerSignature = this.customerDetails.signatureUrl;
    }

    // Customer signature base64
    if (this.customerDetails?.signatureBase64) {
      updateData['lpo.customerSignatureBase64'] = this.customerDetails.signatureBase64;
      if (!this.data.booking.lpo) this.data.booking.lpo = {};
      this.data.booking.lpo.customerSignatureBase64 = this.customerDetails.signatureBase64;
    }

    // Persist digital signature + consent record
    if (this.digitalSignature) {
      updateData['lpo.digitalSignature'] = this.digitalSignature;
    }
    if (this.consentRecord) {
      updateData['lpo.consentRecord'] = this.consentRecord;
    }

    // Status transition: Accepted → Confirmed on LPO submission
    let nextStatus = this.data.booking.status;
    if (nextStatus === 'Accepted') {
      nextStatus = 'Confirmed';
      updateData.status = 'Confirmed';
    }

    await updateDoc(bookingRef, updateData);

    // 2B. Add version to Documents subcollection
    try {
      const lNum = this.data.booking.lpoNumber || 'LPO-GEN';
      const versionsCol = collection(this.afs, `booking-list/${this.data.booking.id}/lpos/${lNum}/versions`);
      const qSnap = await getDocs(query(versionsCol));
      const bookingDocs: any[] = [];
      qSnap.forEach(d => {
        const dData = d.data();
        if (!dData['isDeleted']) {
          bookingDocs.push({ id: d.id, ...dData });
        }
      });

      for (const oldDoc of bookingDocs) {
        if (oldDoc.isCurrent) {
          await updateDoc(doc(this.afs, `booking-list/${this.data.booking.id}/lpos/${lNum}/versions`, oldDoc.id), {
            isCurrent: false
          });
        }
      }

      const version = bookingDocs.length + 1;
      const docId = String(version);
      const lpoUrl = this.data.booking.LPO || this.data.booking.lpoUrl || '';
      const newVersionDoc = {
        id: docId,
        type: 'LPO',
        category: 'Generated',
        name: version === 1 ? `L_${lNum}` : `L_${lNum}_v${version}`,
        documentNumber: lNum,
        version: version,
        url: lpoUrl,
        mimeType: 'application/pdf',
        extension: 'pdf',
        createdAt: now,
        createdBy: this.data.booking.vendorId || 'temp_vendor',
        status: 'Approved',
        isCurrent: true,
        displayOrder: 3,
        generatedFrom: {
          generator: 'LPO',
          bookingId: this.data.booking.id,
          documentNumber: lNum
        },
        actionHistory: [{
          action: version === 1 ? 'Generated' : 'Regenerated',
          performedBy: this.data.booking.vendorId || 'temp_vendor',
          performedAt: now
        }]
      };
      // await setDoc(doc(this.afs, `booking-list/${this.data.booking.id}/documents`, docId), newVersionDoc);

      // --- WRITE TO NEW STRUCTURE (PHASE A) ---
      try {
        const lpoMetaRef = doc(this.afs, `booking-list/${this.data.booking.id}/lpos`, lNum);
        await setDoc(lpoMetaRef, {
          lpoId: lNum,
          lpoNumber: lNum,
          activeVersion: version,
          status: 'Approved',
          updatedAt: now
        }, { merge: true });

        const versionDocRef = doc(this.afs, `booking-list/${this.data.booking.id}/lpos/${lNum}/versions`, String(version));
        await setDoc(versionDocRef, {
          ...newVersionDoc,
          dataSnapshot: {
            lpoNumber: lNum,
            lpoDate: now,
            deliveryCharge: Number(this.deliveryCharge) || 0,
            baseAmount: Number(this.baseAmount) || 0,
            customerSignature: this.customerDetails?.signatureUrl || null,
            digitalSignature: this.digitalSignature || null,
            consentRecord: this.consentRecord || null
          }
        });
      } catch (dualWriteErr) {
        console.error('Error writing to lpos subcollection:', dualWriteErr);
      }
    } catch (err) {
      console.error('Error writing LPO version to documents subcollection:', err);
    }

    await this.rtBooking.updateLpo(
      this.data.booking.id,
      {
        generated: true,
        date: now.toISOString(),
        deliveryCharge: Number(this.deliveryCharge) || 0,
        totalAmount: Number(this.baseAmount) || 0,
        baseAmount: Number(this.baseAmount) || 0,
        customerSignature: this.customerDetails?.signatureUrl || null,
        customerSignatureBase64: this.customerDetails?.signatureBase64 || null,
        rejected: false,
        rejectionReason: '',
      },
      this.auth.currentUser?.uid,
    );

    if (nextStatus !== this.data.booking.status) {
      await this.rtBooking.updateStatus(
        this.data.booking.id,
        nextStatus,
        {},
        this.auth.currentUser?.uid,
      );
    }

    this.data.booking.lpoGenerated = true;
    this.data.booking.lpoRejected = false;
    this.data.booking.lpoDate = now;
    this.data.booking.status = nextStatus;
    this.lpoDate = now;
  }

  capitalize(value: string): string {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }

  async getLpoPdfDefinition(): Promise<TDocumentDefinitions> {
    const booking = this.data.booking;
    const customer = this.customerDetails || {};
    const vendor = this.vendorDetails || {};
    const equip = booking.equipment || {};

    const logoBase =
      this.customerDetails?.companyLogoBase64 || this.customerLogoBase64;
    let logoToUse = logoBase;

    // Retry fetching if missing (Fallback)
    if (!logoToUse && customer.logoUrl) {
      try {
        logoToUse = await this.makeImageCircular(customer.logoUrl);
      } catch (e) {
        console.warn('Fallback failed', e);
      }
    }

    // 2️⃣ STEP 2: Signatures
    const customerSigBase64 =
      this.customerDetails?.signatureBase64 ||
      this.data.booking?.lpo?.customerSignatureBase64 ||
      null;

    const vendorSigBase64 =
      this.vendorDetails?.signatureBase64 ||
      this.data.booking?.lpo?.vendorSignatureBase64 ||
      null;

    const totalAmount =
      this.decimalPipe.transform(booking.totalAmount, '1.2-2') + ' AED';
    const startDate = this.datePipe.transform(
      booking.customer?.startDate?.toDate(),
      'dd MMM yyyy',
    );
    const endDate = this.datePipe.transform(
      booking.customer?.endDate?.toDate(),
      'dd MMM yyyy',
    );
    const lpoDateStr = this.datePipe.transform(this.lpoDate, 'dd MMM yyyy');
    const poNumber =
      'PO-' + (booking.id ? booking.id.slice(-6).toUpperCase() : 'XXXXXX');

    // Helper function to build stacks
    const buildStack = (...items: any[]) =>
      items.filter((item) => item && Object.keys(item).length > 0);

    const docDefinition: any = {
      content: [
        // Header: Logo + Company Info (Simplified)
        {
          columns: [
            logoToUse
              ? {
                image: logoToUse,
                width: 60,
                height: 60,
                margin: [0, 0, 15, 0],
              }
              : null,
            {
              width: '*',
              stack: [
                {
                  text: (customer.companyName || 'Customer Name').toUpperCase(),
                  style: 'headerCompany',
                },
                {
                  text: customer.companyDetails || 'Customer Address',
                  style: 'subHeader',
                },
                {
                  text: `Phno: ${customer.phoneNumber || booking.customer?.phone || '-'}`,
                  style: 'subHeader',
                },
                customer.telephoneNumber
                  ? {
                    text: `Telephone: ${customer.telephoneNumber}`,
                    style: 'subHeader',
                  }
                  : null,
                {
                  text: `Email: ${customer.email || 'N/A'}`,
                  style: 'subHeader',
                },
              ].filter((x) => !!x),
            },
          ].filter((x) => !!x),
          margin: [0, 0, 0, 5],
        },

        // Centered Title
        {
          stack: [
            {
              text: 'PURCHASE ORDER',
              style: 'headerTitle',
              alignment: 'center',
            },
            customer.lpoHeader
              ? {
                text: customer.lpoHeader,
                style: 'customHeader',
                alignment: 'center',
              }
              : null,
          ].filter((x) => !!x),
          margin: [0, 10, 0, 25],
        },

        {
          canvas: [
            {
              type: 'line',
              x1: 0,
              y1: 5,
              x2: 515,
              y2: 5,
              lineWidth: 1.5,
              strokeColor: '#1e40af',
            }, // Slightly thicker primary line
          ],
        },
        { text: '', margin: [0, 0, 0, 20] },

        // Vendor and PO Info
        {
          columns: [
            {
              width: '50%',
              stack: [
                { text: 'TO (Vendor)', style: 'sectionTitle' },
                {
                  text: vendor.displayName || vendor.companyname || 'Vendor Name',
                  style: 'boldText',
                },
                {
                  text: vendor.address || 'Vendor Address',
                  style: 'normalText',
                },
                {
                  text: `Phone: ${vendor.phoneNumber || '-'}`,
                  style: 'normalText',
                },
                {
                  text: `Email: ${vendor.emailid || '-'}`,
                  style: 'normalText',
                },
              ],
            },
            {
              width: '50%',
              stack: [
                {
                  text: 'PO DETAILS',
                  style: 'sectionTitle',
                  alignment: 'right',
                },
                { text: poNumber, style: 'poNumber', alignment: 'right' },
                {
                  text: `Date: ${lpoDateStr}`,
                  style: 'dateText',
                  alignment: 'right',
                },
                customer.trn
                  ? {
                    text: `TRN: ${customer.trn}`,
                    style: 'trnHighlight',
                    alignment: 'right',
                  }
                  : null,
              ].filter((x) => !!x),
            },
          ],
          margin: [0, 0, 0, 30],
        },

        // Booking Details (Full Width)
        {
          stack: [
            { text: 'BOOKING DETAILS', style: 'sectionTitle' },
            booking.equipments && booking.equipments.length > 0
              ? {
                table: {
                  widths: ['30%', '15%', '35%', '20%'],
                  body: [
                    [
                      { text: 'Equipment', bold: true, style: 'smallText' },
                      { text: 'Plate No', bold: true, style: 'smallText' },
                      { text: 'Duration', bold: true, style: 'smallText' },
                      { text: 'Location', bold: true, style: 'smallText' },
                    ],
                    ...booking.equipments.map((eq: any) => {
                      const eqStart = eq.startDate ? (eq.startDate.toDate ? eq.startDate.toDate() : new Date(eq.startDate)) : null;
                      const eqEnd = eq.endDate ? (eq.endDate.toDate ? eq.endDate.toDate() : new Date(eq.endDate)) : null;
                      const eqStartStr = eqStart ? eqStart.toLocaleDateString('en-GB') : '-';
                      const eqEndStr = eqEnd ? eqEnd.toLocaleDateString('en-GB') : '-';
                      return [
                        { text: eq.name || eq.customEquipmentName || '-', style: 'smallText' },
                        { text: eq.plate_number || eq.customPlateNumber || '-', style: 'smallText' },
                        { text: `${eqStartStr} to ${eqEndStr}`, style: 'smallText' },
                        { text: eq.location || eq.emirate || '-', style: 'smallText' },
                      ];
                    })
                  ],
                },
                layout: 'lightHorizontalLines',
              }
              : {
                table: {
                  widths: ['20%', '30%', '20%', '30%'],
                  body: [
                    [
                      { text: 'Equipment:', bold: true, style: 'smallText' },
                      {
                        text: `${equip.name || '-'}\n${equip.brand || ''} ${equip.model || ''}`,
                        style: 'smallText',
                      },
                      { text: 'Plate No:', bold: true, style: 'smallText' },
                      { text: equip.plate_number || '-', style: 'smallText' },
                    ],
                    [
                      { text: 'Duration:', bold: true, style: 'smallText' },
                      {
                        text:
                          booking.customer?.priceType === 'per_trip'
                            ? `${startDate} (${this.duration})`
                            : `${startDate} to ${endDate} (${this.duration})`,
                        style: 'smallText',
                      },
                      { text: 'Location:', bold: true, style: 'smallText' },
                      {
                        text: booking.customer?.location || '-',
                        style: 'smallText',
                      },
                    ],
                  ],
                },
                layout: 'lightHorizontalLines',
              },
          ],
          margin: [0, 0, 0, 30],
        },

        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto'],
            body: (() => {
              const body: any[] = [
                [
                  { text: 'Description', style: 'tableHeader' },
                  { text: 'Rate / Type', style: 'tableHeader' },
                  {
                    text: 'Amount (AED)',
                    style: 'tableHeader',
                    alignment: 'right',
                  },
                ],
              ];

              if (booking.equipments && booking.equipments.length > 0) {
                booking.equipments.forEach((eq: any) => {
                  body.push([
                    {
                      text: `Rental Charges for ${eq.name || eq.customEquipmentName || 'Equipment'}`,
                      style: 'tableCell',
                    },
                    {
                      text: `AED ${this.decimalPipe.transform(eq.price, '1.0-0')} / fixed`,
                      style: 'tableCell',
                    },
                    {
                      text: this.decimalPipe.transform(eq.price, '1.2-2'),
                      style: 'tableCell',
                      alignment: 'right',
                    },
                  ]);
                });
              } else {
                body.push([
                  {
                    text: `Rental Charges for ${equip.name || 'Equipment'}`,
                    style: 'tableCell',
                  },
                  {
                    text: `AED ${this.decimalPipe.transform(this.getEffectiveRate(), '1.0-0')} / ${booking.customer?.priceType || '-'}`,
                    style: 'tableCell',
                  },
                  {
                    text: this.decimalPipe.transform(this.baseAmount, '1.2-2'),
                    style: 'tableCell',
                    alignment: 'right',
                  },
                ]);
              }

              if (this.deliveryCharge > 0) {
                body.push([
                  {
                    text: 'Transport Cost',
                    style: 'tableCell',
                    colSpan: 2,
                  },
                  {},
                  {
                    text: this.decimalPipe.transform(
                      this.deliveryCharge,
                      '1.2-2',
                    ),
                    style: 'tableCell',
                    alignment: 'right',
                  },
                ]);
              }

              body.push([
                { text: 'TOTAL AMOUNT', colSpan: 2, style: 'tableFooter' },
                {},
                {
                  text: this.decimalPipe.transform(
                    this.getFinalTotal(),
                    '1.2-2',
                  ),
                  style: 'tableFooter',
                  alignment: 'right',
                },
              ]);

              return body;
            })(),
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 20],
        },

        { text: 'Terms & Conditions', style: 'sectionTitle' },
        customer.lpoTerms
          ? {
            text: customer.lpoTerms,
            style: 'normalText',
            margin: [0, 0, 0, 20],
          }
          : {
            ul: [
              'This Purchase Order is subject to the terms of service of ALUGULOO.',
              'Rental period and rates are as specified above.',
              'Vendor must ensure the equipment is in good working condition upon delivery.',
            ],
            style: 'normalText',
            margin: [0, 0, 0, 20],
          },

        customer.lpoFooter
          ? {
            text: customer.lpoFooter,
            style: 'footerText',
            margin: [0, 0, 0, 20],
          }
          : { text: '' },

        // Customer Signature + Vendor Signature (if approved/vendor view)
        {
          columns: [
            { width: '*', text: '' },
            {
              width: '50%',
              stack: buildStack(
                customerSigBase64
                  ? {
                    image: customerSigBase64,
                    width: 150,
                    margin: [0, 10, 0, 5],
                  }
                  : null,
                {
                  canvas: [
                    {
                      type: 'line',
                      x1: 0,
                      y1: 0,
                      x2: 200,
                      y2: 0,
                      lineWidth: 1,
                    },
                  ],
                },
                {
                  text: 'Customer Authorized Signature',
                  style: 'sigText',
                  margin: [0, 5, 0, 0],
                  alignment: 'center',
                },
              ),
            },
          ],
          margin: [0, 30, 0, 0],
        },
      ],
      styles: {
        headerCompany: {
          fontSize: 20,
          bold: true,
          color: '#1e40af', // Exact primary-blue
          margin: [0, 0, 0, 4],
        },
        trnHighlight: {
          fontSize: 10,
          bold: true,
          color: '#4b5563', // gray-600
          margin: [0, 2, 0, 2],
        },
        subHeader: { fontSize: 11, color: '#4b5563', margin: [0, 0, 0, 2] },
        headerTitle: {
          fontSize: 24,
          bold: true,
          color: '#1e40af',
          margin: [0, 0, 0, 8],
        },
        customHeader: {
          fontSize: 12,
          italics: true,
          color: '#666666',
          margin: [0, 0, 0, 5],
        },
        poNumber: { fontSize: 12, bold: true, margin: [0, 0, 0, 2] },
        dateText: { fontSize: 10, color: '#555555' },
        sectionTitle: {
          fontSize: 12,
          bold: true,
          color: '#1976d2',
          margin: [0, 0, 0, 5],
        },
        boldText: { fontSize: 11, bold: true, margin: [0, 0, 0, 2] },
        normalText: { fontSize: 10, margin: [0, 0, 0, 2] },
        smallText: { fontSize: 9, color: '#666666' },
        tableHeader: {
          fontSize: 10,
          bold: true,
          fillColor: '#f5f5f5',
          margin: [0, 5, 0, 5],
        },
        tableCell: { fontSize: 10, margin: [0, 5, 0, 5] },
        tableFooter: {
          fontSize: 11,
          bold: true,
          fillColor: '#eeeeee',
          margin: [0, 5, 0, 5],
        },
        footerText: { fontSize: 9, italics: true, color: '#777777' },
        sigText: { fontSize: 10, bold: true },
      },
      defaultStyle: {
        columnGap: 20,
      },
    };

    console.log('✅ PDF definition created successfully');
    return docDefinition;
  }

  async downloadPdf() {
    this.ux.showOverlay('Generating PDF...');
    try {
      // Use the faster and higher quality pdfMake implementation
      const docDef = await this.getLpoPdfDefinition();
      const fileName = `LPO-${this.data.booking.id.slice(-6).toUpperCase()}.pdf`;

      pdfMake.createPdf(docDef).download(fileName);

      this.ux.showSuccess('PDF Downloaded successfully!', 'Downloaded');
    } catch (e) {
      console.error('Download error', e);
      this.ux.showError('Failed to generate PDF');
    } finally {
      this.ux.closeOverlay();
    }
  }

  isImage(url: string | null): boolean {
    if (!url) return false;
    const lowUrl = url.toLowerCase();
    if (lowUrl.startsWith('data:image/')) return true;
    const pathPart = lowUrl.split('?')[0];
    return (
      pathPart.endsWith('.jpg') ||
      pathPart.endsWith('.jpeg') ||
      pathPart.endsWith('.png') ||
      pathPart.endsWith('.gif') ||
      pathPart.endsWith('.webp')
    );
  }

  async approveLpo() {
    this.isSaving = true;
    this.ux.showOverlay('Approving LPO...');

    try {
      console.log('🚀 Starting LPO approval process...');
      const booking = this.data.booking;

      console.log('📋 LPO Status:', {
        isGenerated: !!booking.lpoGenerated,
        isImageUpload: !!booking.LPO,
        lpoUrl: booking.LPO,
      });

      // Approve the LPO and generate PDF for storage
      console.log('✅ Approving LPO...');

      const bookingRef = doc(this.afs, 'booking-list', booking.id);

      const updateData: any = {
        lpoApprovedByVendor: true,
        lpoApprovedAt: new Date(),
        lpoApprovedBy: 'vendor',
        totalAmount: Number(this.baseAmount) || 0,
        deliveryCharge: Number(this.deliveryCharge) || 0,
        discount: Number(this.discount) || 0,
        lpoRejectionReason: null,
        lpoApproved: true,
        status: 'Confirmed',
        priceAdjustmentRejected: false,
        awaitingCustomerPriceConsent: false
      };

      await updateDoc(bookingRef, updateData);

      // Update Realtime DB
      const rtLpoUpdate: any = {
        approved: true,
        approvedByVendor: true,
        priceAdjustmentRejected: false,
        deliveryCharge: Number(this.deliveryCharge) || 0,
        discount: Number(this.discount) || 0,
        totalAmount: this.getFinalTotal(),
        baseAmount: Number(this.baseAmount) || 0,
        awaitingCustomerPriceConsent: false,
        rejectionReason: ""
      };

      await this.rtBooking.updateLpo(
        booking.id,
        rtLpoUpdate,
        this.auth.currentUser?.uid,
      );

      await this.rtBooking.updateStatus(
        booking.id,
        'Confirmed',
        {},
        this.auth.currentUser?.uid,
      );

      // Generate and upload PDF
      let lpoUrl = booking.LPO; // Use uploaded LPO if available

      // If no uploaded LPO, generate one from the current view
      if (!lpoUrl && booking.lpoGenerated) {
        try {
          console.log('📦 Generating PDF from LPO content...');

          // Get the LPO content element
          // Use pdfmake for faster, higher quality generation
          const docDef = await this.getLpoPdfDefinition();
          const pdfBlob = (await pdfMake.createPdf(docDef).getBlob()) as Blob;
          console.log('✅ PDF blob created using pdfMake, size:', pdfBlob.size);

          // Upload to Firebase Storage
          console.log('☁️ Uploading PDF to storage...');
          const filePath = `lpo_approved/${booking.id}_lpo.pdf`;
          const storageRef = ref(this.storage, filePath);
          await uploadBytes(storageRef, pdfBlob);
          lpoUrl = await getDownloadURL(storageRef);
          console.log('✅ PDF uploaded, URL:', lpoUrl);

          // LPO URL will be saved to pdfcollection only, not to booking-list
        } catch (error) {
          console.error('❌ Failed to generate/upload PDF:', error);
          console.log('⚠️ Continuing with approval without PDF URL');
          lpoUrl = null;
        }
      }

      // Save to pdfcollection
      if (lpoUrl) {
        console.log('💾 Saving LPO reference to pdfcollection...');
        const pdfDocRef = doc(this.afs, `pdfcollection/${booking.id}`);
        const pdfSnap = await getDoc(pdfDocRef);
        const existingPdfData = pdfSnap.exists() ? pdfSnap.data() as any : null;
        const qNum = existingPdfData?.quotationNumber ?? (booking.quotationNumber ?? null);
        await setDoc(
          pdfDocRef,
          {
            lpo: {
              pdflink: lpoUrl,
              createdAt: new Date(),
              label: 'Approved LPO',
              approvedAt: new Date(),
            },
            bookingId: booking.id,
            quotationNumber: qNum,
          },
          { merge: true },
        );
        console.log('✅ LPO reference saved to pdfcollection');
      } else {
        console.warn('⚠️ No LPO URL available to save to pdfcollection');
      }

      console.log('🎉 LPO approval complete!');

      // Notify customer
      const customerId = booking.bywhom || booking.customerId;
      if (customerId) {
        let title = 'LPO Approved & Confirmed';
        let msg = `Your LPO for Booking #${booking.id} has been approved.`;
        msg += ` Final total: ${this.decimalPipe.transform(this.getFinalTotal(), '1.2-2')} AED.`;

        this.notificationService.sendNotification(customerId, {
          title: title,
          message: msg,
          type: 'success',
          link: '/booking-orders/details/' + booking.id,
          bookingId: booking.id,
        });
      }

      this.ux.showSuccess('LPO Approved successfully');
      this.dialogRef.close({ approved: true });
    } catch (error) {
      console.error('❌ Error approving LPO:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        error,
      });
      this.ux.showError(
        'Failed to approve LPO: ' +
        (error instanceof Error ? error.message : 'Unknown error'),
      );
    } finally {
      console.log('🔄 Cleaning up...');
      this.isSaving = false;
      this.ux.closeOverlay();
    }
  }

  async rejectLpo() {
    const dialogRef = this.dialog.open(RejectionReasonDialogComponent, {
      width: '400px',
      data: {
        title: 'Reject LPO',
        label: 'Please provide a reason for rejecting this LPO. This feedback will be visible to the customer.',
        buttonText: 'Reject LPO'
      }
    });

    const reason = await dialogRef.afterClosed().toPromise();
    if (!reason) return;

    this.isSaving = true;
    this.ux.showOverlay('Rejecting LPO...');

    try {
      console.log('🚫 Starting LPO rejection process...');
      const booking = this.data.booking;

      // Update booking to mark LPO as rejected
      const bookingRef = doc(this.afs, 'booking-list', booking.id);
      await updateDoc(bookingRef, {
        lpoApproved: false,
        lpoRejected: true,
        lpoRejectionReason: reason,
        lpoRejectedAt: new Date(),
        lpoRejectedBy: 'vendor',
        lpoGenerated: false, // Reset so customer can regenerate
      });

      // Update realtime database
      await this.rtBooking.updateLpo(
        booking.id,
        {
          generated: false,
          approved: false,
          rejected: true,
          rejectionReason: reason,
          rejectedAt: new Date().toISOString(),
        },
        this.auth.currentUser?.uid,
      );

      // Notify customer
      const customerId = booking.bywhom || booking.customerId;
      if (customerId) {
        this.notificationService.sendNotification(customerId, {
          title: 'LPO Rejected',
          message: `Your LPO for Booking #${booking.id} has been rejected by the vendor. Reason: ${reason}`,
          type: 'warning',
          link: '/booking-orders',
          bookingId: booking.id,
        });
      }

      console.log('✅ LPO rejection complete!');
      this.ux.showSuccess('LPO Rejected. Customer has been notified.');
      this.dialogRef.close();
    } catch (error) {
      console.error('❌ Error rejecting LPO:', error);
      this.ux.showError('Failed to reject LPO. Please try again.');
    } finally {
      this.isSaving = false;
      this.ux.instantCloseOverlay();
    }
  }

  protected buildDirtySnapshot(): Record<string, any> {
    if (this.isVendor || this.data.booking.lpoGenerated || this.data.booking.lpoApproved) {
      return {};
    }
    return {
      baseAmount: Number(this.baseAmount) || 0,
      deliveryCharge: Number(this.deliveryCharge) || 0,
      discount: Number(this.discount) || 0,
      consentGiven: this.consentGiven,
      digitalSignature: this.digitalSignature,
      consentRecord: this.consentRecord,
    };
  }

  async close() {
    this.dialogRef.close({ action: 'cancel' });
  }

  triggerSigInput() {
    document.getElementById('lpoSigInput')?.click();
  }

  async onSigSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.ux.showOverlay('Uploading Signature...');
      try {
        // Convert file to base64
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const path = `signatures/${Date.now()}_${file.name}`;
        const storageRef = ref(this.storage, path);
        const uploadResult = await uploadBytes(storageRef, file);
        const url = await getDownloadURL(uploadResult.ref);

        if (!this.customerDetails) this.customerDetails = {};
        this.customerDetails.signatureUrl = url;
        this.customerDetails.signatureBase64 = base64; // Store base64 too
      } catch (error) {
        console.error('Error uploading signature:', error);
        this.ux.showError('Failed to upload signature');
      } finally {
        this.ux.closeOverlay();
      }
    }
  }

  initCanvas() {
    if (this.sigCanvas) {
      const canvas = this.sigCanvas.nativeElement;
      this.ctx = canvas.getContext('2d')!;
      this.ctx.lineWidth = 2;
      this.ctx.lineCap = 'round';
      this.ctx.strokeStyle = '#000000';
    }
  }

  startDrawing(event: MouseEvent) {
    if (!this.ctx) this.initCanvas();
    this.isDrawing = true;
    this.ctx.beginPath();
    this.ctx.moveTo(event.offsetX, event.offsetY);
  }

  draw(event: MouseEvent) {
    if (!this.isDrawing) return;
    this.ctx.lineTo(event.offsetX, event.offsetY);
    this.ctx.stroke();
  }

  stopDrawing() {
    this.isDrawing = false;
    this.ctx.closePath();
  }

  clearCanvas() {
    if (this.sigCanvas) {
      const canvas = this.sigCanvas.nativeElement;
      this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  async saveCanvasSignature() {
    if (!this.sigCanvas) return;
    const canvas = this.sigCanvas.nativeElement;

    this.ux.showOverlay('Saving Signature...');
    const base64Canvas = canvas.toDataURL('image/png');

    // 1️⃣ STEP 1: Persist base64 data immediately
    if (!this.customerDetails) this.customerDetails = {};
    this.customerDetails.signatureBase64 = base64Canvas;

    canvas.toBlob(async (blob) => {
      if (blob) {
        try {
          const path = `signatures/drawn_${Date.now()}.png`;
          const storageRef = ref(this.storage, path);
          const uploadResult = await uploadBytes(storageRef, blob);
          const url = await getDownloadURL(uploadResult.ref);

          // Keep URL for display purposes
          this.customerDetails.signatureUrl = url;
        } catch (error) {
          console.error('Error saving drawn signature:', error);
          this.ux.showError('Failed to save signature');
        } finally {
          this.ux.closeOverlay();
        }
      }
    }, 'image/png');
  }

  // async getBase64FromUrl(url: string): Promise<string> {
  //   try {
  //     const response = await fetch(url);
  //     const blob = await response.blob();
  //     return new Promise((resolve, reject) => {
  //       const reader = new FileReader();
  //       reader.onloadend = () => resolve(reader.result as string);
  //       reader.onerror = reject;
  //       reader.readAsDataURL(blob);
  //     });
  //   } catch (error) {
  //     console.error('Error fetching image for base64 conversion:', error);
  //     throw error;
  //   }
  // }

  async firebaseUrlToBase64(url: string): Promise<string> {
    const response = await fetch(url);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async makeImageCircular(url: string): Promise<string> {
    if (!url) return '';

    // If it's already a data URL, we can use it directly but we still need to make it circular
    if (url.startsWith('data:')) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const size = Math.min(img.width, img.height);
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d')!;
          ctx.beginPath();
          ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(
            img,
            (img.width - size) / 2,
            (img.height - size) / 2,
            size,
            size,
            0,
            0,
            size,
            size,
          );
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = reject;
        img.src = url;
      });
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const size = Math.min(img.width, img.height);
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d')!;

        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(
          img,
          (img.width - size) / 2,
          (img.height - size) / 2,
          size,
          size,
          0,
          0,
          size,
          size,
        );
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = (err) => {
        console.warn('Failed to load image for circular conversion:', url, err);
        // Fallback to simple base64
        this.firebaseUrlToBase64(url)
          .then(resolve)
          .catch(() => resolve(''));
      };

      // Use cache buster only for remote URLs
      const separator = url.includes('?') ? '&' : '?';
      img.src = url + separator + 'cb=' + Date.now();
    });
  }

  onOpenEditorClick() {
    this.dialogRef.close({ action: 'open_editor' });
  }

  printPdf() {
    if (this.data.booking?.LPO) {
      const w = window.open(this.data.booking.LPO, '_blank');
      if (w) {
        w.focus();
        w.print();
      }
    } else {
      window.print();
    }
  }
}